/**
 * How many rows the "Recently Visited" section ended up with last time, per project.
 *
 * The section sits ABOVE the screen list, so its height is the screen list's offset: a placeholder
 * that guesses the count wrong moves every screen row under the pointer when the real rows land.
 * With a 12s answer that window is long enough to click through, and a click then opens the screen
 * that slid into the cursor's place.
 *
 * So the placeholder is only drawn at a height we actually know, and the only honest source for it
 * is what this project showed last time — the count is stable between visits (it is the same user's
 * last six screens) while being unguessable before the answer arrives. With nothing remembered the
 * section draws nothing and waits, which is what it did before there was a placeholder at all.
 */

const STORAGE_KEY_PREFIX: string = "kuika-recently-visited-rows";

/** What `GetRecentlyOpenedScreens` is asked for — no remembered count can exceed it. */
export const RECENTLY_VISITED_MAX_ROWS: number = 6;

const storageKey = (projectId: string): string => `${STORAGE_KEY_PREFIX}:${projectId}`;

/**
 * The rows this project last settled at, or `undefined` when there is nothing trustworthy: no
 * project yet, nothing stored, storage blocked, or a value that is not a count we could have
 * written. `0` is a real answer, not a missing one — a project whose user has visited nothing shows
 * no rows, and remembering that is what stops the placeholder appearing there at all.
 */
export const readRememberedRowCount = (projectId: string | undefined): number | undefined => {
  if (!projectId) {
    return undefined;
  }

  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(storageKey(projectId));
  } catch {
    // Blocked or unavailable: no memory, so no placeholder. The section still fills normally.
    return undefined;
  }

  if (raw === null) {
    return undefined;
  }

  const parsed: number = parseInt(raw, 10);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > RECENTLY_VISITED_MAX_ROWS) {
    return undefined;
  }

  return parsed;
};

export const rememberRowCount = (projectId: string | undefined, count: number): void => {
  if (!projectId || !Number.isInteger(count) || count < 0 || count > RECENTLY_VISITED_MAX_ROWS) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey(projectId), count.toString());
  } catch {
    // Blocked or full: this visit still draws its rows, the next one just opens without a placeholder.
  }
};
